import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TabControlComponent } from '../../shared/controls/tab-control/tab-control.component';
import { TabPageComponent } from '../../shared/controls/tab-control/tab-page.component';
import { SwitchControlComponent } from '../../shared/controls/switch-control/switch-control.component';

import { ToastService } from '../../shared/toast/toast.service';
import { ErrorService } from '../../shared/services/error-service';
import { GroupConstants } from '../../shared/constants/group.constants';

import { UserRoleService, CreateUserRoleInput, CreateUserRolePermissionsInput, Permissions, UpdateUserRoleInput } from '../../../service/proxy';

@Component({
    templateUrl: './create-or-update-user-role.component.html',
    styleUrls: ['./create-or-update-user-role.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        TabControlComponent,
        TabPageComponent,
        SwitchControlComponent
    ],

    providers: [
        UserRoleService
    ],

    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CreateOrUpdateUserRoleComponent implements OnInit {
    public userRoleId: number = 0;
    public userRole: any = {};

    public rolePermissions: { area: string; permissions: string }[] = [];

    public groupedConstants = Object.values(GroupConstants).map(group => ({
        ...group,
        areas: group.areas.map((area: string) => ({
            name: area,
            permissions: {
                Full: false,
                View: false,
                Create: false,
                Edit: false,
                Export: false,
                Delete: false,
                DeletePending: false,
                DeleteCompleted: false
            }
        }))
    }));

    public isSaving: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastService: ToastService,
        private errorService: ErrorService,
        private userRoleService: UserRoleService
    ) {}

    ngOnInit(): void {
        this.route.paramMap
            .subscribe(params => {
                this.userRoleId = Number(params.get('id'));

                if (this.userRoleId > 0) {
                    this.getUserRoleById(this.userRoleId);
                }

                this.getRolePermissions();
            }
        );
    }

    getUserRoleById(id: number): void {
        this.userRoleService.getUserRoleById(id)
            .subscribe(result => {
                this.userRole = result;
            }
        );
    }

    getRolePermissions(): void {
        this.userRoleService.getRolePermissionsById(this.userRoleId)
            .subscribe(result => {
                this.rolePermissions = result.map((item: any) => ({
                    area: item.area,
                    permissions: item.permissions
                }));
            }
        );
    }

    onPermissionChange(area: { name: string; permissions: { [key: string]: boolean } }, permission: string): void {
        if (permission === 'Full') {
            const isFullSelected = area.permissions['Full'];

            Object.keys(area.permissions).forEach(key => {
                if (key !== 'Full') {
                    area.permissions[key] = isFullSelected;
                }
            });
        }
        else {
            if (permission === 'View' && !area.permissions['View']) {
                area.permissions['Create'] = false;
                area.permissions['Edit'] = false;

                if (area.permissions['Export']) {
                    area.permissions['Export'] = false
                };

                if (area.permissions['Delete']) {
                    area.permissions['Delete'] = false
                };

                if (area.permissions['DeletePending']) {
                    area.permissions['DeletePending'] = false
                };

                if (area.permissions['DeleteCompleted']) {
                    area.permissions['DeleteCompleted'] = false
                };

            }
            else if (['Create', 'Edit', 'Export', 'Delete', 'DeletePending', 'DeleteCompleted'].includes(permission) && area.permissions[permission]) {
                area.permissions['View'] = true;
            }

            const allSelected = Object.keys(area.permissions)
                .filter(key => key !== 'Full')
                .every(key => area.permissions[key]);

            area.permissions['Full'] = allSelected;
        }
    }

    savePermissions(group: { areas: { name: string; permissions: { [key: string]: boolean } }[] }): void {
        const permissionsToSave: Permissions[] = group.areas.map(area => {
            const permission = new Permissions();

            permission.area = area.name;
            permission.permissions = Object.keys(area.permissions)
                .filter(key => area.permissions[key])
                .join(',');

            return permission;
        });

        const input = new CreateUserRolePermissionsInput();
        input.permissions = permissionsToSave;

        this.userRoleService.saveRolePermissions(this.userRoleId, input)
            .subscribe({
                next: () => {
                    this.toastService.show('Permissions saved', 'success');
                },
                error: (error) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onSave(form: any): void {
        if (!form.valid) {
            return;
        }

        this.isSaving = true;

        if (this.userRoleId <= 0) {
            this.createUserRole();
        }
        else {
            this.updateUserRole();
        }
    }

    onCancel(): void {
        this.router.navigate(['/user-role']);
    }

    createUserRole(): void {
        const input = this.userRole as CreateUserRoleInput;

        this.userRoleService.createUserRole(input)
            .subscribe(() => {
                this.router.navigate(['/user-role']);
            }
        );
    }

    updateUserRole(): void {
        const input = this.userRole as UpdateUserRoleInput;

        this.userRoleService.updateUserRole(this.userRoleId, input)
            .subscribe(() => {
                this.router.navigate(['/user-role']);
            }
        );
    }
}